import {
  AdditiveBlending,
  CanvasTexture,
  Group,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  type Scene,
} from 'three';
import { VisualRandom } from '../../visual/index.js';
import type {
  QualityContext,
  VisualFieldReader,
  VisualFrameContext,
  VisualMutation,
  VisualRenderer,
  VisualSignalEvent,
  WaveEvent,
} from '../../visual/index.js';

/**
 * The machine, reading itself out.
 *
 * The rule this renderer is built around: **every line is a real value**. Not
 * decorative glyphs, not lorem-ipsum equations — the BPM is the pack's BPM, the
 * combo is the combo, θ is computed from the beat envelope that is on the bus
 * this frame. If the numbers were invented the layer would be wallpaper, and the
 * interesting thing about showing a system's state is that it is the state.
 *
 * Text is drawn into a 2D canvas and uploaded as a texture, which is the cheapest
 * way to get real glyphs into a three scene without a font loader or an SDF
 * pipeline.
 *
 * ## Clusters
 *
 * This used to be one panel. A wave moved it, which meant six waves in a second
 * produced one panel at the newest position and five that had never been seen —
 * the read-out *travelled* rather than *accumulated*, and a passage of dense
 * input looked exactly like a passage of sparse input.
 *
 * So the renderer owns a **pool** of clusters. One is resident: it lives for the
 * whole run, sits where the recipe put it, and stays live, so a quiet passage
 * still shows the machine's state. Every wave takes another from the pool, at
 * the wave's position, in a layout the wave chose. They coexist, they age out,
 * and when the pool is full the oldest is reused rather than a new one being
 * allocated — an unbounded pool of canvases is the one way this layer could take
 * the frame budget with it.
 *
 * A wave-born cluster **freezes** once its burst has passed: it keeps showing
 * what the machine was doing at the instant that note was struck. Still real
 * runtime state, and it bounds the cost — twelve live read-outs each re-rendering
 * a canvas twelve times a second would cost more than everything else in the
 * frame put together. The resident cluster stays live, so there is always one
 * that is telling the truth about *now*.
 *
 * `CODE` shares this implementation. The two differ in which lines they compose,
 * not in how they are drawn, so the mode is a parameter rather than a fork.
 */

export type MathDialect = 'MATH' | 'CODE';

/**
 * How a read-out is arranged in the frame.
 *
 * A fixed panel in the corner is a HUD, and a HUD is not part of a picture — it
 * is a thing laid over one. These are all still the same real values, drawn from
 * the same canvas; what changes is where the text sits, how it is laid out on
 * the sheet, and how the sheet sits in the world.
 *
 * `PANEL` is the original and stays the resident cluster's layout, so a recipe
 * that never asked for anything keeps the composition it had.
 */
export const TEXT_LAYOUTS = [
  'PANEL',
  'FLOATING_CLUSTER',
  'RING_TEXT',
  'ORBITING_FORMULA',
  'RANDOM_HOLOGRAM',
  'VERTICAL_FRAGMENT',
  'PERSPECTIVE_SURFACE',
] as const;
export type TextLayout = (typeof TEXT_LAYOUTS)[number];

export function isTextLayout(value: string): value is TextLayout {
  return (TEXT_LAYOUTS as readonly string[]).includes(value);
}

export interface MathRendererOptions {
  readonly scene: Scene;
  readonly layerId: string;
  readonly fieldRadius: number;
  readonly dialect?: MathDialect;
  /** Supplies values the signal bus does not carry, like the song's name. */
  readonly describe?: () => Readonly<Record<string, string | number>>;
  readonly capacity?: number;
}

const CANVAS_WIDTH = 768;
const CANVAS_HEIGHT = 384;
/** Redraw at most this often. Twelve per second reads as live without costing. */
const REDRAW_INTERVAL = 1 / 12;
/** How long a wave keeps breaking the text apart, in seconds. */
const BURST_SECONDS = 1.1;
const FONT = '22px ui-monospace, "SFMono-Regular", Menlo, Consolas, monospace';
const LINE_HEIGHT = 27;
/**
 * How wide one monospace glyph is at the size above.
 *
 * Measured rather than derived: `measureText` per character during a burst would
 * be hundreds of layout calls a frame, and the font is fixed-width, so one
 * number is exactly as accurate.
 */
const GLYPH_ADVANCE = 13.2;

/** One read-out. Pooled — see the note on the class. */
interface Cluster {
  readonly group: Group;
  readonly canvas: HTMLCanvasElement;
  readonly drawing: CanvasRenderingContext2D;
  readonly texture: CanvasTexture;
  readonly material: MeshBasicMaterial;
  readonly mesh: Mesh;
  active: boolean;
  /** The one that never expires and never freezes. */
  readonly resident: boolean;
  born: number;
  lifetime: number;
  layout: TextLayout;
  anchorX: number;
  anchorY: number;
  orbitPhase: number;
  burst: { born: number; direction: number; strength: number } | null;
  /** Stops redrawing once its wave has passed. See the note on the class. */
  frozen: boolean;
  lastText: string;
  lastRedraw: number;
  seed: number;
  /**
   * Whether this cluster is caught in something that moves it.
   *
   * Set when its wave was shared with a material that publishes a flow. The
   * cluster's anchor is then advected every frame, so the text travels with
   * the plume instead of hanging in it.
   */
  drifting: boolean;
  /** Whether this cluster only shows where something has been painted. */
  masked: boolean;
  /** Radians. How far the flow has tilted this sheet. */
  shear: number;
  /** Field units. Set when a wash decided how wide the ring should be. */
  ringRadius: number;
}

export class MathRenderer implements VisualRenderer {
  readonly id: string;

  private readonly group = new Group();
  private readonly scene: Scene;
  private readonly dialect: MathDialect;
  private readonly describe: () => Readonly<Record<string, string | number>>;
  private readonly fieldRadius: number;
  private readonly baseCapacity: number;
  private capacity: number;

  private clusters: Cluster[] = [];
  private enabled = true;
  private attached = false;
  private lastEventLine = '';

  /**
   * How broken the transmission is.
   *
   * `DISTORT` and `BREAK` raise it; it repairs slowly. Rather than dimming the
   * panel on a miss, it breaks the *text* — characters drop out of the lines,
   * which is a read-out degrading rather than a graphic being faded.
   */
  private fragmentation = 0;
  private orbit = 0;
  private orbitVelocity = 0;
  private noiseTerm = 0;
  private wavesDrawn = 0;
  private clustersReused = 0;
  /** The cluster currently being painted, for the glyph mask. */
  private painting: Cluster | null = null;
  private paintField: VisualFieldReader | null = null;
  private paintScale = 0.62;
  private maskedGlyphs = 0;
  private readonly mutationCounts: Record<string, number> = {};

  constructor(options: MathRendererOptions) {
    this.scene = options.scene;
    this.dialect = options.dialect ?? 'MATH';
    this.id = this.dialect;
    this.fieldRadius = options.fieldRadius;
    this.describe = options.describe ?? (() => ({}));
    this.baseCapacity = options.capacity ?? 4;
    this.capacity = this.baseCapacity;
    this.group.name = `visual:${this.id}:${options.layerId}`;
  }

  /**
   * How many read-outs may be alive at once.
   *
   * The one knob that matters for this renderer's cost: every cluster is a
   * canvas, a texture and a draw call. Only read before the pool is built, so
   * changing quality mid-run cannot reallocate canvases during play.
   */
  setQuality(quality: QualityContext): void {
    if (this.clusters.length > 0) return;
    this.capacity = Math.max(
      1,
      Math.min(14, Math.round(this.baseCapacity * Math.max(0.75, quality.capacityScale))),
    );
  }

  initialize(context: VisualFrameContext): void {
    if (this.clusters.length === 0) {
      for (let index = 0; index < this.capacity; index += 1) {
        this.clusters.push(this.buildCluster(index === 0, context));
      }
    }
    if (!this.attached) {
      this.scene.add(this.group);
      this.attached = true;
    }
  }

  private buildCluster(resident: boolean, context: VisualFrameContext): Cluster {
    const canvas = document.createElement('canvas');
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    const drawing = canvas.getContext('2d');
    if (drawing === null) throw new Error('MathRenderer needs a 2D context');

    const texture = new CanvasTexture(canvas);
    texture.minFilter = LinearFilter;
    texture.magFilter = LinearFilter;

    const material = new MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: AdditiveBlending,
      opacity: resident ? 1 : 0,
    });

    const height = this.fieldRadius * 1.1;
    const mesh = new Mesh(
      new PlaneGeometry(height * (CANVAS_WIDTH / CANVAS_HEIGHT), height),
      material,
    );
    mesh.frustumCulled = false;

    const group = new Group();
    group.add(mesh);
    group.visible = resident;
    this.group.add(group);

    return {
      group, canvas, drawing, texture, material, mesh,
      active: resident,
      resident,
      born: context.time,
      lifetime: resident ? Number.POSITIVE_INFINITY : 8,
      layout: 'PANEL',
      anchorX: -0.15,
      anchorY: 0.62,
      orbitPhase: 0,
      burst: null,
      frozen: false,
      lastText: '',
      lastRedraw: -1,
      seed: 0,
      drifting: false,
      masked: false,
      shear: 0,
      ringRadius: 0,
    };
  }

  reset(): void {
    for (const cluster of this.clusters) {
      cluster.active = cluster.resident;
      cluster.group.visible = cluster.resident;
      cluster.burst = null;
      cluster.frozen = false;
      cluster.lastText = '';
      cluster.lastRedraw = -1;
      cluster.layout = 'PANEL';
      cluster.anchorX = -0.15;
      cluster.anchorY = 0.62;
      cluster.material.opacity = cluster.resident ? 1 : 0;
      cluster.drifting = false;
      cluster.masked = false;
      cluster.shear = 0;
      cluster.ringRadius = 0;
    }
    this.lastEventLine = '';
    this.fragmentation = 0;
    this.orbit = 0;
    this.orbitVelocity = 0;
    this.noiseTerm = 0;
    this.wavesDrawn = 0;
    this.clustersReused = 0;
    this.maskedGlyphs = 0;
    for (const key of Object.keys(this.mutationCounts)) delete this.mutationCounts[key];
  }

  /**
   * Mutations that act on the read-out rather than on the numbers.
   *
   * The displayed values stay runtime-derived throughout — §15 is explicit about
   * that, and inventing figures would make the layer decorative. What changes is
   * how intact the transmission is and where it sits.
   */
  applyMutation(item: VisualMutation, context: VisualFrameContext): void {
    this.mutationCounts[item.action] = (this.mutationCounts[item.action] ?? 0) + 1;
    const force = item.sustained ? item.strength * context.deltaTime : item.strength;

    switch (item.action) {
      case 'BREAK':
      case 'DISTORT':
        this.fragmentation = Math.min(0.85, this.fragmentation + force * 0.3);
        this.invalidate();
        break;
      case 'SHIFT':
        // Off-beat input adds a noise term to the equations — a real extra
        // symbol in the formula, not a visual filter over it.
        this.noiseTerm = Math.min(1, this.noiseTerm + force * 0.5);
        this.invalidate();
        break;
      case 'ROTATE':
        this.orbitVelocity += force * 0.7;
        break;
      case 'MERGE':
      case 'FREEZE':
        this.fragmentation = Math.max(0, this.fragmentation - force * 0.4);
        break;
      default:
        break;
    }
  }

  /** Forces the live clusters to repaint even if every value reads the same. */
  private invalidate(): void {
    for (const cluster of this.clusters) {
      if (cluster.active && !cluster.frozen) cluster.lastText = '';
    }
  }

  get mutationsApplied(): Readonly<Record<string, number>> {
    return { ...this.mutationCounts };
  }

  get fragmentationLevel(): number {
    return this.fragmentation;
  }

  handleSignal(event: VisualSignalEvent): void {
    // The most recent moment gets its own line, so the panel shows what just
    // happened rather than only what is continuously true.
    if (event.name === 'NOTE_HIT') {
      const grade = typeof event.detail.grade === 'string' ? event.detail.grade : 'hit';
      const offset = typeof event.detail.offsetMs === 'number' ? event.detail.offsetMs : 0;
      this.lastEventLine =
        this.dialect === 'CODE'
          ? `resolveWave("${grade}", ${offset.toFixed(1)}ms)`
          : `judge = ${grade.toUpperCase()}  Δt = ${offset >= 0 ? '+' : ''}${offset.toFixed(1)} ms`;
      return;
    }
    if (event.name === 'NOTE_MISS') {
      this.lastEventLine = this.dialect === 'CODE' ? 'expireWave() // miss' : 'judge = MISS';
    }
  }

  /**
   * A wave, as text.
   *
   * A cluster is taken from the pool, placed where the disturbance happened, and
   * torn apart: glyphs travel outward from the centre of its sheet, spread, and
   * fade over about a second. The wave is expressed as *the text itself being
   * disturbed* rather than as decoration drawn near it — the same event moves
   * air as smoke, and moves characters as text.
   *
   * The values never stop being real. A fragmenting read-out is still showing
   * the live BPM and the live combo; it is showing them badly, which is the
   * point.
   */
  spawnWave(event: WaveEvent, context: VisualFrameContext): void {
    const cluster = this.takeCluster(context.time);
    if (cluster === null) return;
    const stream = new VisualRandom(event.seed);

    cluster.active = true;
    cluster.group.visible = true;
    cluster.born = context.time;
    cluster.seed = event.seed;
    cluster.frozen = false;
    cluster.lastText = '';
    cluster.lastRedraw = -1;
    cluster.anchorX = event.x;
    cluster.anchorY = event.y;
    cluster.orbitPhase = stream.range(0, Math.PI * 2);
    cluster.lifetime = event.lifetime * 2.2 + 4;

    // Who else is answering this disturbance. Read as names, so this renderer
    // never holds a reference to another — see `WaveCollision`.
    const group = event.collision?.materials ?? [];
    cluster.drifting = group.includes('SMOKE');
    cluster.masked = group.includes('INK_PAINT');
    const onWash = group.includes('WATERCOLOR');

    // A recipe may pin the layout; otherwise the collision decides, and
    // failing that the event picks one — the same one every time it is
    // replayed.
    const pinned = context.layer.parameters.layout;
    if (typeof pinned === 'string' && isTextLayout(pinned)) {
      cluster.layout = pinned;
    } else if (onWash) {
      // A wash is a round, soft region with an edge that matters. Text laid
      // around that edge belongs to it; a tilted panel dropped on top of it
      // would just be two things in one place.
      cluster.layout = 'RING_TEXT';
      cluster.ringRadius = 0.22 * event.scale;
    } else if (cluster.drifting) {
      // Something loose enough to be carried.
      cluster.layout = stream.bool() ? 'FLOATING_CLUSTER' : 'VERTICAL_FRAGMENT';
    } else {
      // PANEL is excluded: an event that moved a read-out across the frame and
      // then drew it as the same corner panel has said nothing.
      const choices = TEXT_LAYOUTS.filter((entry) => entry !== 'PANEL');
      cluster.layout = choices[stream.int(0, choices.length)] as TextLayout;
    }

    cluster.burst = {
      born: context.time,
      direction: event.direction,
      strength: clamp01(event.strength) * event.scale * (event.style === 'torn' ? 1.4 : 1),
    };
    // A torn wave leaves the whole read-out damaged after the burst has passed.
    if (event.style === 'torn') this.fragmentation = Math.min(0.9, this.fragmentation + 0.25);
    this.wavesDrawn += 1;
  }

  /**
   * A free cluster, or the oldest one that is not resident.
   *
   * Reuse rather than refusal: a full pool that dropped waves would make dense
   * passages *quieter* than sparse ones, which is backwards. The resident is
   * never taken — losing it would leave the frame with no live read-out at all.
   */
  private takeCluster(time: number): Cluster | null {
    let oldest: Cluster | null = null;
    for (const cluster of this.clusters) {
      if (!cluster.active) return cluster;
      if (cluster.resident) continue;
      if (oldest === null || cluster.born < oldest.born) oldest = cluster;
    }
    if (oldest !== null) this.clustersReused += 1;
    void time;
    return oldest;
  }

  update(context: VisualFrameContext): void {
    if (!this.enabled || this.clusters.length === 0) return;

    // Damage repairs itself slowly, so a run that settles down reads clearly
    // again rather than staying broken for good.
    this.fragmentation = Math.max(0, this.fragmentation - context.deltaTime * 0.06);
    this.noiseTerm = Math.max(0, this.noiseTerm - context.deltaTime * 0.08);
    this.orbitVelocity *= Math.exp(-context.deltaTime * 1.2);
    this.orbit += this.orbitVelocity * context.deltaTime;

    const layerOpacity = clamp01(context.layer.opacity * context.layer.intensity);

    for (const cluster of this.clusters) {
      if (!cluster.active) continue;

      const age = cluster.resident ? 0 : (context.time - cluster.born) / cluster.lifetime;
      if (age >= 1) {
        cluster.active = false;
        cluster.group.visible = false;
        continue;
      }

      // Fades in quickly and out slowly: a read-out that snapped on would read
      // as a glitch, and one that snapped off would read as a dropped frame.
      const fade = cluster.resident
        ? 1
        : Math.min(1, age / 0.06) * (age > 0.75 ? 1 - (age - 0.75) / 0.25 : 1);
      cluster.material.opacity = layerOpacity * fade;

      if (cluster.drifting) {
        // Carried by whatever published a flow here. The cluster is not told
        // that it is smoke; it is told which way this point is moving.
        const here = context.field.sample(cluster.anchorX, cluster.anchorY);
        cluster.anchorX += here.flow[0] * context.deltaTime;
        cluster.anchorY += here.flow[1] * context.deltaTime;
        // Sheared as well as moved: the flow tilts the sheet, so the text
        // leans the way the plume is going.
        cluster.shear = Math.atan2(here.flow[1], here.flow[0]) * 0.25;
      }

      this.place(context, cluster);

      if (cluster.frozen) continue;
      if (cluster.lastRedraw >= 0 && context.time - cluster.lastRedraw < REDRAW_INTERVAL) continue;
      cluster.lastRedraw = context.time;

      const bursting = cluster.burst !== null;
      const lines = this.fragment(this.compose(context), context);
      const text = lines.join('\n');
      // The upload is the expensive half; skip it when nothing reads
      // differently. A burst is the exception: the values may be identical
      // frame to frame while the glyphs are still travelling, and skipping
      // those frames would freeze the wave halfway through it.
      if (text === cluster.lastText && !bursting) continue;
      cluster.lastText = text;
      this.paint(lines, context, cluster);

      // Its wave has passed. What it is showing now is what the machine was
      // doing when that note was struck, and it stops costing anything.
      if (!cluster.resident && !bursting) cluster.frozen = true;
    }
  }

  /** Real values, formatted. Nothing here is invented. */
  private compose(context: VisualFrameContext): string[] {
    const signals = context.signals;
    const extra = this.describe();
    const beatPhase = signals.BEAT;

    if (this.dialect === 'CODE') {
      return [
        `// ${String(extra.song ?? 'untitled')}`,
        `songTime = ${context.time.toFixed(3)}`,
        `bpm = ${signals.BPM.toFixed(1)}`,
        `combo += ${signals.COMBO > 0 ? 1 : 0}   // combo = ${signals.COMBO.toFixed(0)}`,
        `accuracy = ${signals.ACCURACY.toFixed(4)}`,
        `score = ${signals.SCORE.toFixed(0)}`,
        `amplitude = ${signals.AMPLITUDE.toFixed(3)}`,
        `bass = ${signals.BASS.toFixed(3)}  mid = ${signals.MID.toFixed(3)}  treble = ${signals.TREBLE.toFixed(3)}`,
        'spawnRipple()',
        'applyBeatForce()',
        'updateMandala()',
        this.lastEventLine,
      ].filter((line) => line.length > 0);
    }

    const omega = (signals.BPM / 60) * Math.PI * 2;
    // Off-beat presses add a real extra term, present in the formula only while
    // the player is striking off the grid.
    const noise = this.noiseTerm > 0.01
      ? [`+ η(t) = ${(this.noiseTerm * Math.sin(context.time * 7.3)).toFixed(3)}   [off-beat]`]
      : [];
    return [
      `BPM = ${signals.BPM.toFixed(1)}`,
      `COMBO = ${signals.COMBO.toFixed(0)}`,
      `ACCURACY = ${signals.ACCURACY.toFixed(3)}`,
      `SCORE = ${signals.SCORE.toFixed(0)}`,
      `t = ${context.time.toFixed(2)} s`,
      `θ = beat × 2π = ${(beatPhase * Math.PI * 2).toFixed(3)}`,
      `ω = 2π·BPM/60 = ${omega.toFixed(3)} rad/s`,
      `r = A sin(ωt + φ) = ${(signals.AMPLITUDE * Math.sin(omega * context.time)).toFixed(3)}`,
      `A = ${signals.AMPLITUDE.toFixed(3)}`,
      `[bass mid treble] = [${signals.BASS.toFixed(2)} ${signals.MID.toFixed(2)} ${signals.TREBLE.toFixed(2)}]`,
      `chaos = ${context.art.chaos.toFixed(2)}  distortion = ${context.art.distortion.toFixed(2)}`,
      ...noise,
      this.lastEventLine,
    ].filter((line) => line.length > 0);
  }

  /**
   * Breaks the read-out apart, in proportion to accumulated damage.
   *
   * Characters are replaced rather than removed so the layout holds still — a
   * line that reflows every frame reads as animation, whereas one that decays in
   * place reads as a signal being lost. The random stream is the layer's own, so
   * the same run damages the same characters.
   */
  private fragment(lines: string[], context: VisualFrameContext): string[] {
    // The artwork's own chaos contributes, so a disordered run degrades the
    // read-out even when this layer was never mutated directly.
    const level = Math.min(0.9, this.fragmentation + context.art.chaos * 0.25);
    if (level <= 0.01) return lines;

    const glyphs = ['█', '▓', '▒', '░', '·', ' '];
    return lines.map((line) =>
      [...line]
        .map((character) =>
          character === ' ' || context.random.next() > level
            ? character
            : (glyphs[context.random.int(0, glyphs.length)] as string),
        )
        .join(''),
    );
  }

  private paint(lines: string[], context: VisualFrameContext, cluster: Cluster): void {
    // Held for the glyph mask, which needs to ask the field where each
    // character would land in the world.
    this.painting = cluster;
    this.paintField = cluster.masked ? context.field : null;
    this.paintScale = numeric(context.layer.parameters.scale, 0.62);
    const drawing = cluster.drawing;
    drawing.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    drawing.font = FONT;
    drawing.textBaseline = 'top';

    const hue = numeric(context.layer.parameters.hue, this.dialect === 'CODE' ? 150 : 185);
    const progress = this.burstProgress(context.time, cluster);
    const burst = cluster.burst;

    switch (cluster.layout) {
      case 'RING_TEXT':
        this.paintRing(drawing, lines, hue, burst, progress);
        break;
      case 'VERTICAL_FRAGMENT':
        this.paintVertical(drawing, lines, hue, burst, progress);
        break;
      case 'FLOATING_CLUSTER':
        this.paintCluster(drawing, lines, hue, burst, progress);
        break;
      case 'RANDOM_HOLOGRAM':
        this.paintHologram(drawing, lines, hue, burst, progress);
        break;
      default:
        // PANEL, ORBITING_FORMULA and PERSPECTIVE_SURFACE share the block
        // layout; what separates them is how the sheet sits in the world, which
        // is `place`. Drawing them differently as well would be two changes
        // doing one job.
        this.paintBlock(drawing, lines, hue, burst, progress);
        break;
    }

    cluster.texture.needsUpdate = true;
  }

  /** 0 before a burst, 1 once it has finished. Clears the burst when done. */
  private burstProgress(time: number, cluster: Cluster): number {
    if (cluster.burst === null) return 1;
    const progress = (time - cluster.burst.born) / BURST_SECONDS;
    if (progress >= 1) {
      cluster.burst = null;
      return 1;
    }
    return progress < 0 ? 0 : progress;
  }

  /**
   * One character, displaced by however much of the wave has reached it.
   *
   * This is the whole of the wave expression for this material. A glyph is
   * pushed outward from the centre of the sheet, further the longer the burst
   * has been running, with a little extra along the direction the disturbance
   * was travelling — so the text tears apart *away from the impact* rather than
   * simply jittering — and fades as it goes.
   */
  private glyph(
    drawing: CanvasRenderingContext2D,
    character: string,
    x: number,
    y: number,
    alpha: number,
    hue: number,
    burst: { born: number; direction: number; strength: number } | null,
    progress: number,
  ): void {
    if (character === ' ') return;

    // Revealed only where something has been painted.
    //
    // The first real use of the shared field for masking: this cluster asks
    // what is at the point this character would occupy, and stays away if the
    // answer is bare surface. What it draws is therefore a *readout of the
    // paint* — the text exists only inside the stain, and grows as the stain
    // does.
    if (this.paintField !== null && this.painting !== null) {
      const spot = this.canvasToField(x, y, this.painting);
      if (this.paintField.sample(spot.x, spot.y).coverage < 0.12) {
        this.maskedGlyphs += 1;
        return;
      }
    }

    let drawX = x;
    let drawY = y;
    let fade = alpha;

    if (burst !== null && progress < 1) {
      const dx = x - CANVAS_WIDTH / 2;
      const dy = y - CANVAS_HEIGHT / 2;
      const distance = Math.hypot(dx, dy) + 1;
      // Eases out, so the tear is violent at the moment of impact and then
      // drifts — linear travel reads as a slide rather than as a break.
      const travel = (1 - (1 - progress) * (1 - progress)) * burst.strength * 165;
      drawX += (dx / distance) * travel + Math.cos(burst.direction) * travel * 0.35;
      drawY += (dy / distance) * travel - Math.sin(burst.direction) * travel * 0.35;
      fade *= 1 - progress * 0.85;
    }

    drawing.fillStyle = `hsla(${hue}, 72%, 72%, ${clamp01(fade)})`;
    drawing.fillText(character, drawX, drawY);
  }

  /**
   * Where a point on a cluster's sheet lands in the world.
   *
   * The sheet is a plane of a known size at a known place, so this is the
   * plane's own transform applied to a canvas coordinate — and it has to
   * match `place`, or the mask would be offset from the paint it is masking.
   */
  private canvasToField(x: number, y: number, cluster: Cluster): { x: number; y: number } {
    const height = 1.1 * this.paintScale;
    const width = height * (CANVAS_WIDTH / CANVAS_HEIGHT);
    return {
      x: cluster.anchorX + (x / CANVAS_WIDTH - 0.5) * width,
      // Canvas y runs down; the world runs up.
      y: cluster.anchorY - (y / CANVAS_HEIGHT - 0.5) * height,
    };
  }

  /** A run of characters, laid out on a row. */
  private glyphRun(
    drawing: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    alpha: number,
    hue: number,
    burst: { born: number; direction: number; strength: number } | null,
    progress: number,
  ): void {
    // Drawn per character only while a wave is passing. The rest of the time a
    // single fillText is a fraction of the cost and looks identical.
    // A masked cluster always goes per character: the whole point is that
    // part of the line is missing.
    if ((burst === null || progress >= 1) && this.paintField === null) {
      drawing.fillStyle = `hsla(${hue}, 72%, 72%, ${clamp01(alpha)})`;
      drawing.fillText(text, x, y);
      return;
    }
    for (let index = 0; index < text.length; index += 1) {
      this.glyph(
        drawing, text[index] as string, x + index * GLYPH_ADVANCE, y, alpha, hue, burst, progress,
      );
    }
  }

  /** The original: left-aligned lines, centred vertically. */
  private paintBlock(
    drawing: CanvasRenderingContext2D,
    lines: string[],
    hue: number,
    burst: { born: number; direction: number; strength: number } | null,
    progress: number,
  ): void {
    const top = (CANVAS_HEIGHT - lines.length * LINE_HEIGHT) / 2;
    lines.forEach((line, index) => {
      // The most recent moment is the brightest line, so the eye finds it.
      const isEvent = index === lines.length - 1 && line === this.lastEventLine;
      const alpha = isEvent ? 0.95 : 0.42 + 0.3 * (1 - index / lines.length);
      this.glyphRun(
        drawing, line, 34, top + index * LINE_HEIGHT, alpha, isEvent ? hue + 40 : hue,
        burst, progress,
      );
    });
  }

  /** Lines scattered across the sheet, each at its own depth. */
  private paintCluster(
    drawing: CanvasRenderingContext2D,
    lines: string[],
    hue: number,
    burst: { born: number; direction: number; strength: number } | null,
    progress: number,
  ): void {
    lines.forEach((line, index) => {
      // Positioned by the line index rather than by a random draw, so the
      // cluster is stable frame to frame — a layout that reshuffles every
      // redraw is a flicker, not a composition.
      const spread = hash01(index * 7 + 3);
      const height = hash01(index * 13 + 11);
      const x = 28 + spread * (CANVAS_WIDTH - 340);
      const y = 18 + height * (CANVAS_HEIGHT - 60);
      this.glyphRun(drawing, line, x, y, 0.35 + hash01(index * 5) * 0.5, hue, burst, progress);
    });
  }

  /** The read-out wrapped around a circle. */
  private paintRing(
    drawing: CanvasRenderingContext2D,
    lines: string[],
    hue: number,
    burst: { born: number; direction: number; strength: number } | null,
    progress: number,
  ): void {
    const text = lines.join('  ·  ');
    const centreX = CANVAS_WIDTH / 2;
    const centreY = CANVAS_HEIGHT / 2;
    const radius = CANVAS_HEIGHT * 0.38;
    const step = (Math.PI * 2) / Math.max(text.length, 1);

    drawing.save();
    drawing.textAlign = 'center';
    for (let index = 0; index < text.length; index += 1) {
      const character = text[index] as string;
      if (character === ' ') continue;
      const angle = index * step - Math.PI / 2;
      const x = centreX + Math.cos(angle) * radius;
      const y = centreY + Math.sin(angle) * radius;
      drawing.save();
      drawing.translate(x, y);
      // Each glyph stands upright on the circle rather than lying along it.
      drawing.rotate(angle + Math.PI / 2);
      this.glyph(drawing, character, 0, 0, 0.75, hue, burst, progress);
      drawing.restore();
    }
    drawing.restore();
    drawing.textAlign = 'left';
  }

  /** Narrow columns running down the sheet, one character to a row. */
  private paintVertical(
    drawing: CanvasRenderingContext2D,
    lines: string[],
    hue: number,
    burst: { born: number; direction: number; strength: number } | null,
    progress: number,
  ): void {
    const columns = Math.min(lines.length, 8);
    const columnWidth = CANVAS_WIDTH / (columns + 1);
    for (let column = 0; column < columns; column += 1) {
      const line = lines[column] as string;
      const x = columnWidth * (column + 0.7);
      const rows = Math.min(line.length, 12);
      for (let row = 0; row < rows; row += 1) {
        const y = 16 + row * LINE_HEIGHT;
        // Fading downward, so each column reads as falling rather than as a
        // list rotated ninety degrees.
        const alpha = 0.85 - (row / rows) * 0.6;
        this.glyph(drawing, line[row] as string, x, y, alpha, hue, burst, progress);
      }
    }
  }

  /** Scanlines and a colour-split ghost. */
  private paintHologram(
    drawing: CanvasRenderingContext2D,
    lines: string[],
    hue: number,
    burst: { born: number; direction: number; strength: number } | null,
    progress: number,
  ): void {
    const top = (CANVAS_HEIGHT - lines.length * LINE_HEIGHT) / 2;

    // The ghost first, so the true text sits on top of it.
    lines.forEach((line, index) => {
      this.glyphRun(drawing, line, 33 + 6, top + index * LINE_HEIGHT + 2, 0.3, hue + 150, burst, progress);
    });
    lines.forEach((line, index) => {
      this.glyphRun(drawing, line, 33, top + index * LINE_HEIGHT, 0.8, hue, burst, progress);
    });

    // Scanlines, drawn over everything. Cheap, and the one cue that reads as
    // "projected" rather than "printed" at any size.
    drawing.fillStyle = `hsla(${hue}, 60%, 80%, 0.06)`;
    for (let y = 0; y < CANVAS_HEIGHT; y += 4) drawing.fillRect(0, y, CANVAS_WIDTH, 2);
  }

  /**
   * Where a cluster's sheet sits, and how.
   *
   * The anchor is a wave's doing; the layout decides what happens around it.
   * `PANEL` keeps the original behaviour — off to one side, facing the camera,
   * and specifically *not* over the middle, because the centre is where notes
   * are judged and a text plane across it would be the art engine making the
   * game harder to play.
   */
  private place(context: VisualFrameContext, cluster: Cluster): void {
    const offsetX = cluster.resident
      ? numeric(context.layer.parameters.offsetX, cluster.anchorX)
      : cluster.anchorX;
    const offsetY = cluster.resident
      ? numeric(context.layer.parameters.offsetY, cluster.anchorY)
      : cluster.anchorY;
    const baseScale = numeric(context.layer.parameters.scale, 0.62);
    const time = context.time;

    // Reset every frame: a layout that does not set a rotation must not inherit
    // one from the layout before it.
    cluster.group.rotation.set(0, 0, 0);
    let x = this.fieldRadius * offsetX;
    let y = this.fieldRadius * offsetY;
    let scale = baseScale;

    switch (cluster.layout) {
      case 'ORBITING_FORMULA': {
        // Travels around its anchor rather than sitting on it.
        const angle = cluster.orbitPhase + this.orbit + time * 0.35;
        x += Math.cos(angle) * this.fieldRadius * 0.24;
        y += Math.sin(angle) * this.fieldRadius * 0.16;
        scale *= 0.72;
        break;
      }
      case 'PERSPECTIVE_SURFACE':
        // Laid back into the scene, so the text recedes instead of facing the
        // viewer squarely. The yaw keeps it from being a symmetric trapezoid.
        cluster.group.rotation.set(-0.95, 0.28, 0);
        scale *= 1.5;
        break;
      case 'RING_TEXT':
        cluster.group.rotation.set(0, 0, time * 0.12 + cluster.orbitPhase);
        // Sized to whatever it is wrapped around, when something asked.
        scale *= cluster.ringRadius > 0 ? 1.1 + cluster.ringRadius * 2.4 : 1.1;
        break;
      case 'VERTICAL_FRAGMENT':
        scale *= 0.9;
        break;
      case 'RANDOM_HOLOGRAM':
        // A slight, constant list. Enough to read as projected rather than
        // mounted, not enough to make the text hard to read.
        cluster.group.rotation.set(0.12, -0.3, 0.03);
        scale *= 0.95;
        break;
      case 'FLOATING_CLUSTER':
        scale *= 1.05;
        break;
      default:
        // PANEL. `ROTATE` walks it around its anchor rather than spinning it,
        // so the text stays readable while its position drifts.
        x += Math.cos(this.orbit) * this.fieldRadius * 0.08;
        y += Math.sin(this.orbit) * this.fieldRadius * 0.05;
        break;
    }

    // Tilted by the flow it is caught in, on top of whatever the layout did.
    if (cluster.drifting) cluster.group.rotation.z += cluster.shear;

    cluster.group.position.set(x, y, 0.4);
    cluster.group.scale.setScalar(scale);
  }

  render(): void {
    // Drawn by the stage's single render, with everything else in the scene.
  }

  setParameters(): void {
    // Read per frame from the resolved layer state; nothing to latch.
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.group.visible = enabled;
  }

  get metrics(): {
    readonly dialect: MathDialect;
    readonly capacity: number;
    /** How many read-outs are on screen right now. */
    readonly activeClusters: number;
    readonly reused: number;
    readonly layouts: readonly TextLayout[];
    readonly waves: number;
    readonly fragmentation: number;
    readonly bursting: number;
    readonly drifting: number;
    readonly masked: number;
    readonly maskedGlyphs: number;
    readonly anchors: readonly { readonly x: number; readonly y: number }[];
    readonly mutations: Readonly<Record<string, number>>;
  } {
    const live = this.clusters.filter((cluster) => cluster.active);
    return {
      dialect: this.dialect,
      capacity: this.capacity,
      activeClusters: live.length,
      reused: this.clustersReused,
      layouts: live.map((cluster) => cluster.layout),
      waves: this.wavesDrawn,
      fragmentation: Number(this.fragmentation.toFixed(3)),
      bursting: live.filter((cluster) => cluster.burst !== null).length,
      drifting: live.filter((cluster) => cluster.drifting).length,
      masked: live.filter((cluster) => cluster.masked).length,
      maskedGlyphs: this.maskedGlyphs,
      anchors: live.map((cluster) => ({
        x: Number(cluster.anchorX.toFixed(4)),
        y: Number(cluster.anchorY.toFixed(4)),
      })),
      mutations: { ...this.mutationCounts },
    };
  }

  dispose(): void {
    for (const cluster of this.clusters) {
      cluster.mesh.geometry.dispose();
      cluster.material.dispose();
      cluster.texture.dispose();
      cluster.group.clear();
    }
    this.clusters = [];
    this.group.clear();
    if (this.attached) {
      this.scene.remove(this.group);
      this.attached = false;
    }
  }
}

function numeric(value: number | string | boolean | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/**
 * A stable pseudo-random number for an integer.
 *
 * Not `VisualRandom`: this decides where line four of a layout sits, which has
 * to be the *same* every redraw or the text jitters. A stream would give a
 * different answer each frame, which is the opposite of what is wanted here.
 */
function hash01(value: number): number {
  const x = Math.sin(value * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}
